// TTS-related types. The shape is provider-agnostic so we can swap in
// server-generated audio or a local AI voice later without touching the reader.

export type TTSSegmentLevel = "sentence" | "paragraph";
export type TTSMode = "classic" | "natural";
/** Provider identities. "server" is the Piper neural server at /tts/;
 *  "kokoro" is the Kokoro-82M server at /tts-kokoro/ — different
 *  endpoint, different voice catalog, different default voice.
 *  "auto" tries the server first and falls back to browser on failure. */
export type TTSProviderName = "auto" | "server" | "kokoro" | "browser";

export interface TTSSettings {
  /** Which provider to use. Defaults to "auto" — Piper server with browser
   *  speech as a fallback. */
  provider: TTSProviderName;
  voiceURI?: string; // browser provider only
  serverVoice?: string; // server provider only, e.g. "en_US-amy-medium"
  rate: number; // 0.5 .. 2.0
  pitch: number; // 0.5 .. 2.0
  volume: number; // 0..1
  mode: TTSMode;
  segmentLevel: TTSSegmentLevel;
}

export const defaultTTSSettings: TTSSettings = {
  // Default to KOKORO (set 2026-06-26 after voice quality A/B). Kokoro is
  // the best-sounding voice in the stack — the preferred default
  // even with the ~3 s cold-start latency the countdown overlay now
  // covers. Per-book prefs override on book reopen.
  provider: "kokoro",
  voiceURI: undefined,
  serverVoice: undefined,
  rate: 1.0,
  pitch: 1.0,
  volume: 1.0,
  mode: "natural",
  segmentLevel: "paragraph",
};

/** A speakable chunk extracted from the current chapter. */
export interface TextSegment {
  id: string;
  bookId: string;
  spineIndex: number;
  href: string;
  text: string;
  cfiStart?: string;
  cfiEnd?: string;
  /** CSS selector inside the rendered iframe to find this segment for highlighting. */
  elementSelector?: string;
  paragraphIndex?: number;
  sentenceIndex?: number;
  /** Source DOM tag (uppercase, e.g. "P", "H1"). Used by TTS start-of-play
   *  logic to skip past chapter/title headings into the first prose
   *  paragraph rather than reading the title aloud. Optional for back-
   *  compat with older segment producers. */
  tag?: string;
}

export interface TTSOptions {
  rate: number;
  pitch: number;
  volume: number;
  /** Browser-provider specific voice. Ignored by the server provider. */
  voice?: SpeechSynthesisVoice;
  /** Server-provider specific voice (e.g. "en_US-amy-medium"). Ignored
   *  by the browser provider. */
  serverVoice?: string;
  /** Fires per spoken word (or sentence on some engines). charIndex/length
   *  are offsets within the segment text. */
  onBoundary?: (charIndex: number, charLength: number) => void;
}

/** Provider-agnostic TTS interface. */
export interface TTSProvider {
  name: string;
  speakSegment(segment: TextSegment, options: TTSOptions): Promise<void>;
  pause(): void;
  resume(): void;
  stop(): void;
  /** Optional warmup — providers that synthesize audio from a remote
   *  server expose this so the next paragraph is ready before the
   *  current one finishes speaking. Browser TTS doesn't need it. */
  prefetch?(segment: TextSegment, options: TTSOptions): void;
  /** Optional iOS-Safari audio unlock — called synchronously from the
   *  Play tap so a real user gesture activates the shared <audio> element.
   *  Without this, Safari blocks programmatic play() that happens after an
   *  await (network fetch), which manifests as "audio stalls / no sound".
   *  Browser (speechSynthesis) TTS has its own primer and ignores this. */
  unlock?(): void;
}
